import classNames from 'classnames'
import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { useSessionLanguage } from '@app/context/SessionSettingsContext'

import { useNavigate } from '@lib/react-router-dom'

import { RewardProgramWidget } from '@pages/RewardsHub/RewardProgramWidget'
import { RewardPrograms } from '@pages/RewardsHub/RewardPrograms'
import { TradingPoolProgress } from '@pages/RewardsHub/components/TradingPoolProgress'
import { useRewardPrograms } from '@pages/RewardsHub/hooks/useRewardPrograms'

import { Carousel } from '@ui/Carousel/Carousel'
import { CarouselChild } from '@ui/Carousel/CarouselItem'
import { Body1, Caption1, Title1 } from '@ui/Typography'
import { Button } from '@ui/components/Button/Button'
import { TradingViewIcon } from '@ui/icons/platforms/TradingView.icon'

import { useWindowResize } from '@utils/domUtils'
import { getTradingViewPromotionUrl } from '@utils/rewardsHubUrls'

import {
  ClientContestDto,
  ClientRebateAccountTypeDto,
  ClientRebateTiersDto,
  MarginBonusDto,
  RewardsHubDashboardDto,
  TradingViewCouponRewardDto,
} from '@model/RewardsHubDashboardDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'

import { MorePerks } from './components/MorePerks'
import { RebateCampaignProgressBar } from './components/RebateCampaignProgressBar'
import { TradingViewProgressBar } from './components/TradingViewProgressBar'

import styles from './ActiveRewards.module.scss'

function getRemainingTime(endDate: Date | string, t: (key: string) => string) {
  const now = new Date()
  const end = new Date(endDate)
  let diff = Math.max(0, end.getTime() - now.getTime())

  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  diff -= days * (1000 * 60 * 60 * 24)

  const hours = Math.floor(diff / (1000 * 60 * 60))
  diff -= hours * (1000 * 60 * 60)

  const minutes = Math.floor(diff / (1000 * 60))

  return `${days}${t('RewardsHub.Days')} ${hours}${t('RewardsHub.Hours')} ${minutes}${t(
    'RewardsHub.Minutes'
  )} `
}

interface ActiveRewardProps {
  rewardsHubDashboard: RewardsHubDashboardDto | undefined
}

export const ActiveRewards: React.FC<ActiveRewardProps> = ({ rewardsHubDashboard }) => {
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const { rewardPrograms } = useRewardPrograms()

  if (!rewardsHubDashboard) return null
  const { campaignsInProgress, campaignsResults } = rewardsHubDashboard

  const activeItemCount =
    (campaignsInProgress.tradingViewCouponReward ? 1 : 0) +
    (campaignsInProgress.marginBonus ? 1 : 0) +
    campaignsInProgress.clientContests.length +
    (campaignsInProgress.clientRebateTiers ? 1 : 0) +
    (campaignsInProgress.clientRebateAccountType ? 1 : 0)

  const showMorePerks = activeItemCount <= 1

  return isMobile ? (
    <>
      <Carousel loop={false} interval={90000}>
        {rewardPrograms?.items.map((rewardP, index) => (
          <CarouselChild key={index}>
            <RewardProgramWidget key={rewardP.id} rewardProgram={rewardP} />
          </CarouselChild>
        ))}
        {campaignsInProgress.tradingViewCouponReward && (
          <CarouselChild>
            <TradingViewCouponReward
              tradingViewCouponReward={campaignsInProgress.tradingViewCouponReward}
            />
          </CarouselChild>
        )}
        {campaignsInProgress.marginBonus && (
          <CarouselChild>
            <MarginBonusReward marginBonus={campaignsInProgress.marginBonus} />
          </CarouselChild>
        )}
        {campaignsInProgress.clientContests.map((contest, index) => (
          <CarouselChild key={index}>
            <ClientContestReward clientContest={contest} />
          </CarouselChild>
        ))}
        {campaignsInProgress.clientRebateTiers && (
          <CarouselChild>
            <TieredRebateCampaignReward clientRebateTiers={campaignsInProgress.clientRebateTiers} />
          </CarouselChild>
        )}
        {campaignsInProgress.clientRebateAccountType && (
          <CarouselChild>
            <FixedRebateCampaignReward
              clientRebateAccountType={campaignsInProgress.clientRebateAccountType}
            />
          </CarouselChild>
        )}
        {campaignsInProgress?.tradingPools.map((contest, index) => (
          <CarouselChild>
            <TradingPoolProgress key={index} campaign={contest} />
          </CarouselChild>
        ))}
        {campaignsResults?.tradingPools.map((contest, index) => (
          <CarouselChild>
            <TradingPoolProgress key={index} campaign={contest} />
          </CarouselChild>
        ))}
        {showMorePerks && (
          <CarouselChild>
            <MorePerks
              title={t('RewardsHub.More Perks Coming Soon')}
              body={t(
                'RewardsHub.Stay tuned, we are constantly working to bring you more value. This space will soon feature new professional tools and rewards that you can unlock through your trading.'
              )}
            />
          </CarouselChild>
        )}
      </Carousel>
    </>
  ) : (
    <Box flex align='flex-start' gap='x16' wrap>
      <RewardPrograms />
      {campaignsInProgress.tradingViewCouponReward && (
        <TradingViewCouponReward
          tradingViewCouponReward={campaignsInProgress.tradingViewCouponReward}
        />
      )}
      {campaignsInProgress.marginBonus && (
        <MarginBonusReward marginBonus={campaignsInProgress.marginBonus} />
      )}
      {campaignsInProgress.clientContests.map((contest, index) => (
        <ClientContestReward key={index} clientContest={contest} />
      ))}
      {campaignsInProgress.clientRebateTiers && (
        <TieredRebateCampaignReward clientRebateTiers={campaignsInProgress.clientRebateTiers} />
      )}
      {campaignsInProgress.clientRebateAccountType && (
        <FixedRebateCampaignReward
          clientRebateAccountType={campaignsInProgress.clientRebateAccountType}
        />
      )}
      {campaignsInProgress?.tradingPools.map((contest, index) => (
        <TradingPoolProgress key={index} campaign={contest} />
      ))}
      {campaignsResults?.tradingPools.map((contest, index) => (
        <TradingPoolProgress key={index} campaign={contest} />
      ))}
      {showMorePerks && (
        <MorePerks
          title={t('RewardsHub.More Perks Coming Soon')}
          body={t(
            'RewardsHub.Stay tuned, we are constantly working to bring you more value. This space will soon feature new professional tools and rewards that you can unlock through your trading.'
          )}
        />
      )}
    </Box>
  )
}

const TradingViewCouponReward: FC<{ tradingViewCouponReward: TradingViewCouponRewardDto }> = ({
  tradingViewCouponReward,
}) => {
  const { t } = useTranslation()
  const title = t('RewardsHub.TradingView Coupon Reward')
  const locale = useSessionLanguage()
  const MARKETING_URL = getTradingViewPromotionUrl(locale)

  const handleClick = () => {
    window.open(MARKETING_URL, '_blank', 'noopener,noreferrer')
  }

  return (
    <Box className={styles.rewardItem} flex direction='column' justify='space-between'>
      <Box flex direction='column' gap='x16'>
        <Box flex gap='x4' align='center'>
          <TradingViewIcon size={24} />
          <Title1 semibold color='text-high-em'>
            {title}
          </Title1>
        </Box>
        <Body1 color='text-med-em'>
          {t(
            'RewardsHub.Unlock more powerful charting tools as you trade. Earned volume resets monthly.'
          )}
        </Body1>
      </Box>
      <TradingViewProgressBar monthlyTradedVolume={tradingViewCouponReward.monthlyTradedVolume} />
      <Box alignSelf='flex-start'>
        <Button.Secondary size='sm' onClick={handleClick}>
          {t('RewardsHub.Learn More')}
        </Button.Secondary>
      </Box>
    </Box>
  )
}

const MarginBonusReward: FC<{ marginBonus: MarginBonusDto }> = ({ marginBonus }) => {
  const { t } = useTranslation()
  const { currency, netDeposit, netCredit, campaign, termsAndConditionsUrl, endDate } = marginBonus

  const { formatMoney, formatNumber } = useFormatNumber()

  const handleClick = () => {
    window.open(termsAndConditionsUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <Box
      className={classNames(styles.rewardItem, styles.marginBonus)}
      flex
      direction='column'
      justify='space-between'
      gap='x16'
    >
      <Title1 semibold color='text-high-em'>
        {campaign.name}
      </Title1>
      <Box flex direction='column' align='flex-start' alignSelf='stretch' gap='x8'>
        <Box flex direction='column' align='flex-start' alignSelf='stretch'>
          <Caption1 color='text-med-em'>{t('RewardsHub.Net Deposits')}</Caption1>
          <Body1 color='text-high-em'>{formatMoney(netDeposit, currency.id)}</Body1>
        </Box>
        <Box flex direction='column' align='flex-start' alignSelf='stretch'>
          <Caption1 color='text-med-em'>{t('RewardsHub.Net Bonus Credit')}</Caption1>
          <Body1 color='text-high-em'>{formatNumber(netCredit)}</Body1>
        </Box>
        <Box flex direction='column' align='flex-start' alignSelf='stretch'>
          <Caption1 color='text-med-em'>{t('RewardsHub.Remaining Time')}</Caption1>
          <Body1 color='text-high-em'>{getRemainingTime(endDate, t)}</Body1>
        </Box>
      </Box>
      <Box alignSelf='flex-start'>
        <Button.Primary size='sm' onClick={handleClick}>
          {t('RewardsHub.Learn More')}
        </Button.Primary>
      </Box>
    </Box>
  )
}

const ClientContestReward: FC<{ clientContest: ClientContestDto }> = ({ clientContest }) => {
  const { t } = useTranslation()
  const { campaign, termsAndConditionsUrl, contestPoints, rank, tradingAccount } = clientContest
  const navigate = useNavigate()

  const { formatNumber } = useFormatNumber()

  const handleLearnMoreClick = () => {
    window.open(termsAndConditionsUrl, '_blank', 'noopener,noreferrer')
  }

  const handleViewMoreClick = () => {
    navigate(
      `/dashboard/traders-room/campaign-results/contest/${tradingAccount.name}/${campaign.id}/`
    )
  }

  return (
    <Box
      className={classNames(styles.rewardItem, styles.campaignContest, {
        [styles.roiPositive]: contestPoints >= 0,
        [styles.roiNegative]: contestPoints < 0,
      })}
      flex
      direction='column'
      justify='space-between'
      gap='x16'
    >
      <Title1 semibold color='text-high-em'>
        {campaign.name}
      </Title1>
      <Box flex direction='column' align='flex-start' alignSelf='stretch' gap='x8'>
        <Box flex direction='column' align='flex-start' alignSelf='stretch'>
          <Caption1 color='text-med-em'>{t('RewardsHub.Current Rank')}</Caption1>
          <Body1 color='text-high-em'>{rank}</Body1>
        </Box>
        <Box flex direction='column' align='flex-start' alignSelf='stretch'>
          <Caption1 color='text-med-em'>{t('RewardsHub.ROI')}</Caption1>
          <Body1 color='text-high-em'>{`${formatNumber(contestPoints)}%`}</Body1>
        </Box>
      </Box>
      <Box flex align='flex-start' gap='x16' alignSelf='flex-start'>
        <Button.Secondary size='sm' onClick={handleLearnMoreClick}>
          {t('RewardsHub.Learn More')}
        </Button.Secondary>
        <Button.Primary size='sm' onClick={handleViewMoreClick}>
          {t('RewardsHub.View More')}
        </Button.Primary>
      </Box>
    </Box>
  )
}

const TieredRebateCampaignReward: FC<{ clientRebateTiers: ClientRebateTiersDto }> = ({
  clientRebateTiers,
}) => {
  const { t } = useTranslation()
  const { campaign, termsAndConditionsUrl, currentTier, currency, tiers, tradingAccount } =
    clientRebateTiers
  const navigate = useNavigate()

  const { formatMoney } = useFormatNumber()

  const rebateRate =
    currentTier === 0 ? 0 : tiers.find((tier) => tier.tier === currentTier)?.multiplier || 0

  const handleLearnMoreClick = () => {
    window.open(termsAndConditionsUrl, '_blank', 'noopener,noreferrer')
  }

  const handleViewMoreClick = () => {
    navigate(
      `/dashboard/traders-room/campaign-results/rebate/${tradingAccount.name}/${campaign.id}/`
    )
  }

  return (
    <Box
      className={classNames(styles.rewardItem, styles.tieredCampaign)}
      flex
      direction='column'
      justify='space-between'
      gap='x16'
    >
      <Title1 semibold color='text-high-em'>
        {campaign.name}
      </Title1>
      <Box flex direction='column' align='flex-start' alignSelf='stretch' gap='x8'>
        <Box flex direction='column' align='flex-start' alignSelf='stretch'>
          <Caption1 color='text-med-em'>{t('RewardsHub.Current Tier')}</Caption1>
          <Body1 color='text-high-em'>{currentTier}</Body1>
        </Box>
        <Box flex direction='column' align='flex-start' alignSelf='stretch'>
          <Caption1 color='text-med-em'>{t('RewardsHub.Rebate Rate')}</Caption1>
          <Body1 color='text-high-em'>{`${formatMoney(rebateRate, currency.id)} ${t(
            'RewardsHub.per lot'
          )}`}</Body1>
        </Box>
      </Box>
      <RebateCampaignProgressBar currentTier={currentTier} />
      <Box flex align='flex-start' gap='x16' alignSelf='flex-start'>
        <Button.Secondary size='sm' onClick={handleLearnMoreClick}>
          {t('RewardsHub.Learn More')}
        </Button.Secondary>
        <Button.Primary size='sm' onClick={handleViewMoreClick}>
          {t('RewardsHub.View Details')}
        </Button.Primary>
      </Box>
    </Box>
  )
}

const FixedRebateCampaignReward: FC<{ clientRebateAccountType: ClientRebateAccountTypeDto }> = ({
  clientRebateAccountType,
}) => {
  const { t } = useTranslation()
  const { campaign, termsAndConditionsUrl, currency, cashbackEarned, tradingAccount, multiplier } =
    clientRebateAccountType
  const navigate = useNavigate()

  const { formatMoney } = useFormatNumber()

  const handleLearnMoreClick = () => {
    window.open(termsAndConditionsUrl, '_blank', 'noopener,noreferrer')
  }

  const handleViewDetailsClick = () => {
    navigate(
      `/dashboard/traders-room/campaign-results/rebate/${tradingAccount.name}/${campaign.id}/`
    )
  }

  return (
    <Box
      className={classNames(styles.rewardItem, styles.fixedRebateCampaign)}
      flex
      direction='column'
      justify='space-between'
      gap='x16'
    >
      <Title1 semibold color='text-high-em'>
        {campaign.name}
      </Title1>
      <Box flex direction='column' gap='x8' alignSelf='stretch' fullHeight>
        <Box flex direction='column' align='flex-start' alignSelf='stretch' gap='x8'>
          <Box flex direction='column' align='flex-start' alignSelf='stretch'>
            <Caption1 color='text-med-em'>{t('RewardsHub.Rebate Rate')}</Caption1>
            <Body1 color='text-high-em'>{`${formatMoney(multiplier, currency.id)} ${t(
              'RewardsHub.per lot'
            )}`}</Body1>
          </Box>
        </Box>
        <Box flex direction='column' align='flex-start' alignSelf='stretch' gap='x8'>
          <Box flex direction='column' align='flex-start' alignSelf='stretch'>
            <Caption1 color='text-med-em'>{t('RewardsHub.Cashback earned')}</Caption1>
            <Body1 color='text-success-high-em' semibold>
              {formatMoney(cashbackEarned, currency.id)}
            </Body1>
          </Box>
        </Box>
      </Box>
      <Box flex align='flex-start' gap='x16' alignSelf='flex-start'>
        <Button.Secondary size='sm' onClick={handleLearnMoreClick}>
          {t('RewardsHub.Learn More')}
        </Button.Secondary>
        <Button.Primary size='sm' onClick={handleViewDetailsClick}>
          {t('RewardsHub.View Details')}
        </Button.Primary>
      </Box>
    </Box>
  )
}
